import { useState, type ChangeEvent, type ReactNode } from 'react';
import { Aperture, Check, ChevronDown, ChevronUp, Hash, ImagePlus, Plus, SlidersHorizontal, Sparkles, Trash2, Volume2, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { THEMES } from '@/features/theme/themes';
import { themeAssetExists } from '@/features/theme/themeAssets';
import { ThemeThumbnail } from '@/features/theme/components/ThemeThumbnail';
import { COMMUNITY_ICONS, iconById } from '@/lib/communityIcons';
import { CHANNEL_EFFECTS, type ChannelEffect } from '@/lib/channelEffects';
import {
  BACKDROP_DIMS,
  BG_TINTS,
  CARD_STYLES,
  CORNER_STYLES,
  DENSITIES,
  FONTS,
  FONT_STACK,
  MESSAGE_STYLES,
  NAME_STYLES,
  RAIL_WIDTHS,
  STUDIO_ACCENTS,
  newChannelId,
  type ChannelKind,
  type Selection,
  type StudioChannel,
  type StudioState,
} from './studioModel';
import {
  Block,
  CheckRow,
  ColorField,
  Drop,
  Hint,
  InspectorSection,
  Row,
  Seg,
  TextField,
} from './inspector/controls';

const TITLES: Record<Selection, string> = {
  page: 'Page',
  community: 'Community',
  channels: 'Channels',
  chat: 'Chat',
  members: 'Members',
};

interface StudioInspectorProps {
  state: StudioState;
  selected: Selection;
  onChange: (patch: Partial<StudioState>, coalesceKey?: string) => void;
  /** Async upload path (storage-backed for signed-in users; local for guests). */
  onUploadImage: (file: File, kind: 'background' | 'banner') => void;
}

/** Right rail — Figma-grade contextual properties for the selected region. */
export function StudioInspector({ state, selected, onChange, onUploadImage }: StudioInspectorProps) {
  return (
    <aside className="flex w-72 flex-none flex-col border-l border-white/[0.08] bg-ink-900">
      <div className="flex items-center gap-2 border-b border-white/[0.05] px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.8px] text-white/40">
        <SlidersHorizontal size={12} strokeWidth={2.4} />
        {TITLES[selected]}
      </div>

      <div className="flex-1 overflow-y-auto pb-6">
        {selected === 'page' && <PageInspector state={state} onChange={onChange} onUploadImage={onUploadImage} />}
        {selected === 'community' && (
          <CommunityInspector state={state} onChange={onChange} onUploadImage={onUploadImage} />
        )}
        {selected === 'channels' && (
          <InspectorSection title="Channels">
            <ChannelsEditor channels={state.channels} onChange={(channels, key) => onChange({ channels }, key)} />
          </InspectorSection>
        )}
        {selected === 'chat' && (
          <div className="p-4">
            <Hint>
              The chat — messages, header, and composer — fills in from your real channel once the
              community is live. It inherits the page accent, font, corners &amp; surface.
            </Hint>
          </div>
        )}
        {selected === 'members' && (
          <div className="p-4">
            <Hint>
              The members list is populated from your real members. Style it from the page accent &amp;
              surface settings.
            </Hint>
          </div>
        )}
      </div>
    </aside>
  );
}

function PageInspector({
  state,
  onChange,
  onUploadImage,
}: {
  state: StudioState;
  onChange: (p: Partial<StudioState>, coalesceKey?: string) => void;
  onUploadImage: (file: File, kind: 'background' | 'banner') => void;
}) {
  function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onUploadImage(file, 'background');
    e.target.value = '';
  }

  return (
    <>
      <InspectorSection title="Background">
        <div className="grid grid-cols-3 gap-1.5">
          {THEMES.map((theme) => {
            const unavailable = theme.file != null && !themeAssetExists(theme.file);
            const active = state.themeId === theme.id;
            return (
              <button
                key={theme.id}
                type="button"
                disabled={unavailable}
                onClick={() => onChange({ themeId: theme.id })}
                title={theme.name}
                className={cn(
                  'relative aspect-video overflow-hidden rounded-[6px] border transition-all',
                  active ? 'border-accent shadow-[0_0_0_1px_var(--winr-accent-soft)]' : 'border-white/[0.08] hover:border-white/30',
                  unavailable && 'cursor-not-allowed opacity-40',
                )}
              >
                <ThemeThumbnail theme={theme} className="absolute inset-0" />
                {active && (
                  <span className="absolute right-0.5 top-0.5 grid h-4 w-4 place-items-center rounded-full bg-accent text-black">
                    <Check size={9} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
          <label
            className={cn(
              'flex aspect-video cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[6px] border border-dashed text-white/35 transition-colors hover:text-hi',
              state.themeId === 'custom' ? 'border-accent text-accent' : 'border-white/15',
            )}
            title="Upload a background"
          >
            <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={onUpload} />
            <ImagePlus size={14} strokeWidth={1.8} />
            <span className="text-[9px] font-bold">Upload</span>
          </label>
        </div>
        <Row label="Dim">
          <Seg options={BACKDROP_DIMS} value={state.dim} onChange={(dim) => onChange({ dim })} />
        </Row>
        <Row label="Tint">
          <Seg options={BG_TINTS} value={state.bgTint} onChange={(bgTint) => onChange({ bgTint })} />
        </Row>
      </InspectorSection>

      <InspectorSection title="Brand">
        <div className="flex flex-wrap items-center gap-1.5">
          {STUDIO_ACCENTS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Accent ${c}`}
              onClick={() => onChange({ accent: c })}
              className={cn(
                'h-6 w-6 rounded-[5px] ring-1 ring-inset ring-white/10 transition-transform hover:scale-105',
                state.accent === c && 'ring-2 ring-white',
              )}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <Row label="Custom">
          <ColorField value={state.accent} onChange={(accent) => onChange({ accent }, 'accent')} />
        </Row>
        <Row label="Font">
          <Drop
            value={state.font}
            options={FONTS}
            onChange={(font) => onChange({ font })}
            render={(o) => <span style={{ fontFamily: FONT_STACK[o.id] }}>{o.label}</span>}
          />
        </Row>
      </InspectorSection>

      <InspectorSection title="Surface">
        <Row label="Material">
          <Seg options={CARD_STYLES} value={state.cardStyle} onChange={(cardStyle) => onChange({ cardStyle })} />
        </Row>
        <Row label="Corners">
          <Seg options={CORNER_STYLES} value={state.corners} onChange={(corners) => onChange({ corners })} />
        </Row>
        <Row label="Spacing">
          <Seg options={DENSITIES} value={state.density} onChange={(density) => onChange({ density })} />
        </Row>
      </InspectorSection>

      <InspectorSection title="Layout">
        <Row label="Rail">
          <Seg options={RAIL_WIDTHS} value={state.railWidth} onChange={(railWidth) => onChange({ railWidth })} />
        </Row>
        <Row label="Messages">
          <Seg options={MESSAGE_STYLES} value={state.messageStyle} onChange={(messageStyle) => onChange({ messageStyle })} />
        </Row>
      </InspectorSection>

      <InspectorSection title="Effects">
        <CheckRow icon={<Wand2 size={13} />} label="Accent glow" checked={state.glow} onChange={(glow) => onChange({ glow })} />
        <CheckRow icon={<Sparkles size={13} />} label="Animations" checked={state.animated} onChange={(animated) => onChange({ animated })} />
        <CheckRow icon={<Aperture size={13} />} label="Vignette" checked={state.vignette} onChange={(vignette) => onChange({ vignette })} />
      </InspectorSection>
    </>
  );
}

function CommunityInspector({
  state,
  onChange,
  onUploadImage,
}: {
  state: StudioState;
  onChange: (p: Partial<StudioState>, coalesceKey?: string) => void;
  onUploadImage: (file: File, kind: 'background' | 'banner') => void;
}) {
  return (
    <>
      <InspectorSection title="Banner">
        <BannerControl
          banner={state.banner}
          onRemove={() => onChange({ banner: null })}
          onUpload={(file) => onUploadImage(file, 'banner')}
        />
      </InspectorSection>
      <InspectorSection title="Identity">
        <Block label="Community name">
          <TextField value={state.communityName} maxLength={40} placeholder="Your community" onChange={(v) => onChange({ communityName: v }, 'communityName')} />
        </Block>
        <Row label="Name style">
          <Seg options={NAME_STYLES} value={state.nameStyle} onChange={(nameStyle) => onChange({ nameStyle })} />
        </Row>
        <Block label="Tagline">
          <TextField value={state.tagline} maxLength={80} placeholder="A space for your people." onChange={(v) => onChange({ tagline: v }, 'tagline')} />
        </Block>
        <Block label="Join button label">
          <TextField value={state.joinLabel} maxLength={24} placeholder="Join community" onChange={(v) => onChange({ joinLabel: v }, 'joinLabel')} />
        </Block>
      </InspectorSection>
    </>
  );
}

function ChannelsEditor({
  channels,
  onChange,
}: {
  channels: StudioChannel[];
  onChange: (channels: StudioChannel[], coalesceKey?: string) => void;
}) {
  const update = (id: string, patch: Partial<StudioChannel>, coalesceKey?: string) =>
    onChange(channels.map((c) => (c.id === id ? { ...c, ...patch } : c)), coalesceKey);
  const remove = (id: string) => onChange(channels.filter((c) => c.id !== id));
  const add = () =>
    onChange([
      ...channels,
      { id: newChannelId(), name: 'new-channel', icon: 'hash', kind: 'text', section: 'TEXT CHANNELS', effect: 'none' },
    ]);
  const move = (id: string, dir: -1 | 1) => {
    const i = channels.findIndex((c) => c.id === id);
    const j = i + dir;
    const arr = [...channels];
    const a = arr[i];
    const b = arr[j];
    if (!a || !b) return;
    arr[i] = b;
    arr[j] = a;
    onChange(arr);
  };

  return (
    <div className="space-y-1.5">
      {channels.map((ch, i) => (
        <div key={ch.id} className="flex items-center gap-1">
          <div className="flex flex-none flex-col">
            <button
              type="button"
              aria-label="Move up"
              disabled={i === 0}
              onClick={() => move(ch.id, -1)}
              className="grid h-[14px] w-4 place-items-center rounded text-white/35 transition-colors hover:text-hi disabled:opacity-25"
            >
              <ChevronUp size={11} />
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={i === channels.length - 1}
              onClick={() => move(ch.id, 1)}
              className="grid h-[14px] w-4 place-items-center rounded text-white/35 transition-colors hover:text-hi disabled:opacity-25"
            >
              <ChevronDown size={11} />
            </button>
          </div>
          <IconPicker value={ch.icon} onChange={(icon) => update(ch.id, { icon })} />
          <input
            type="text"
            value={ch.name}
            maxLength={24}
            placeholder="channel"
            onChange={(e) => update(ch.id, { name: e.target.value }, `ch-name-${ch.id}`)}
            className="h-7 min-w-0 flex-1 rounded-[6px] border border-white/[0.08] bg-white/[0.045] px-2 text-[12px] font-medium text-hi outline-none transition-colors placeholder:text-white/25 focus:border-accent/70"
          />
          <KindToggle kind={ch.kind} onChange={(kind) => update(ch.id, { kind })} />
          <EffectPicker value={ch.effect} onChange={(effect) => update(ch.id, { effect })} />
          <button
            type="button"
            aria-label="Remove channel"
            disabled={channels.length <= 1}
            onClick={() => remove(ch.id)}
            className="grid h-7 w-6 flex-none place-items-center rounded-md text-white/35 transition-colors hover:bg-white/10 hover:text-hi disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex w-full items-center justify-center gap-1.5 rounded-[6px] border border-dashed border-white/15 py-1.5 text-[11.5px] font-bold text-white/50 transition-colors hover:border-white/30 hover:text-hi"
      >
        <Plus size={12} strokeWidth={2.4} /> Add channel
      </button>
    </div>
  );
}

function BannerControl({
  banner,
  onRemove,
  onUpload: onUploadFile,
}: {
  banner: string | null;
  onRemove: () => void;
  onUpload: (file: File) => void;
}) {
  function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onUploadFile(file);
    e.target.value = '';
  }
  return (
    <div className="space-y-2">
      <div className="aspect-[16/6] overflow-hidden rounded-[8px] border border-white/[0.08] bg-black/30">
        {banner ? (
          <img src={banner} alt="Community banner" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full place-items-center text-[11px] font-semibold text-white/30">No banner</div>
        )}
      </div>
      <div className="flex gap-1.5">
        <label className="flex h-7 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-[6px] border border-white/[0.12] text-[11.5px] font-bold text-white/60 transition-colors hover:border-white/30 hover:text-hi">
          <input type="file" accept="image/*" className="sr-only" onChange={onUpload} />
          <ImagePlus size={12} strokeWidth={2.2} /> {banner ? 'Replace' : 'Upload'}
        </label>
        {banner && (
          <button
            type="button"
            aria-label="Remove banner"
            onClick={onRemove}
            className="grid h-7 w-9 place-items-center rounded-[6px] border border-white/[0.12] text-white/60 transition-colors hover:text-hi"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function EffectPicker({ value, onChange }: { value: ChannelEffect; onChange: (e: ChannelEffect) => void }) {
  const [open, setOpen] = useState(false);
  const current = CHANNEL_EFFECTS.find((e) => e.id === value) ?? CHANNEL_EFFECTS[0]!;
  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Channel effect: ${current.label}`}
        className="grid h-7 w-7 place-items-center rounded-[6px] border border-white/[0.1] bg-black/30 text-white/50 transition-colors hover:text-hi"
      >
        {current.emoji ? <span className="text-[13px] leading-none">{current.emoji}</span> : <Sparkles size={12} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-40 mt-1 w-40 rounded-[8px] border border-white/[0.1] bg-ink-800 p-1 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
            {CHANNEL_EFFECTS.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => {
                  onChange(e.id);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] font-semibold transition-colors',
                  e.id === value ? 'bg-accent/20 text-accent' : 'text-hi hover:bg-white/[0.06]',
                )}
              >
                <span className="w-4 text-center text-[13px] leading-none">{e.emoji || '∅'}</span>
                {e.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function KindToggle({ kind, onChange }: { kind: ChannelKind; onChange: (k: ChannelKind) => void }) {
  const isText = kind === 'text';
  return (
    <button
      type="button"
      title={isText ? 'Text channel — switch to voice' : 'Voice channel — switch to text'}
      onClick={() => onChange(isText ? 'voice' : 'text')}
      className="grid h-7 w-7 flex-none place-items-center rounded-[6px] border border-white/[0.1] bg-black/30 text-white/50 transition-colors hover:text-hi"
    >
      {isText ? <Hash size={13} /> : <Volume2 size={13} className="text-[#4ade80]" />}
    </button>
  );
}

function IconPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const Current = iconById(value);
  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Choose icon"
        className="grid h-7 w-7 place-items-center rounded-[6px] border border-white/[0.1] bg-black/30 text-hi transition-colors hover:border-white/30"
      >
        <Current size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-40 mt-1 grid w-[232px] grid-cols-6 gap-1 rounded-[8px] border border-white/[0.1] bg-ink-800 p-2 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
            {COMMUNITY_ICONS.map(({ id, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  onChange(id);
                  setOpen(false);
                }}
                className={cn(
                  'grid h-8 w-8 place-items-center rounded-md text-white/50 transition-colors hover:bg-white/10 hover:text-hi',
                  id === value && 'bg-accent/20 text-accent',
                )}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Kept for potential external use; the kit's Hint is used internally. */
export function InspectorHint({ children }: { children: ReactNode }) {
  return <Hint>{children}</Hint>;
}
